import {Component, ChangeDetectionStrategy} from '@angular/core';
import {NgOptimizedImage} from "@angular/common";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ConfirmPasswordDirective} from "@app/_directives/confirm-password.directive";
import {PasswordRequirementDirective} from "@app/_directives/password-requirement.directive";
import {ResetPasswordRequest} from "@app/_models/api";
import {AuthService} from "@app/_services/auth.service";
import {ToastService} from "@app/_services/toast.service";
import {Router} from "@angular/router";

interface ResetPasswordForm {
    email: FormControl<string | null>;
    password: FormControl<string | null>;
    confirmPassword: FormControl<string | null>;
    verificationCode: FormControl<number | null>;
}

@Component({
    selector: 'app-reset-password',
    imports: [
        NgOptimizedImage,
        ReactiveFormsModule
    ],
    templateUrl: './reset-password.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent {
    formGroup: FormGroup<ResetPasswordForm>;
    loading = false;
    passwordFieldType: boolean = true;
    confirmPasswordFieldType: boolean = true;

    constructor(
        private router: Router,
        private confirmPasswordValidator: ConfirmPasswordDirective,
        private passwordRequirementValidator: PasswordRequirementDirective,
        private authService: AuthService,
        private toastService: ToastService
    ) {
        this.formGroup = new FormGroup<ResetPasswordForm>(
            {
                email: new FormControl("", [Validators.required, Validators.email]),
                password: new FormControl("", [Validators.required, this.passwordRequirementValidator.validate.bind(this.passwordRequirementValidator)]),
                confirmPassword: new FormControl("", Validators.required),
                verificationCode: new FormControl<number | null>(null, [Validators.required]),
            },
            {validators: [this.confirmPasswordValidator.validate.bind(this.confirmPasswordValidator)]});
    }

    checkPasswordForError(error: string): boolean {
        const password = this.formGroup.controls['password'].value;
        if (password == null || password == "") {
            return true;
        }

        if (!this.formGroup.controls['password'].hasError('weakPassword')) {
            return false;
        }

        const errors: string[] = this.formGroup.controls['password'].getError('weakPassword');
        return errors.some(value => value === error);
    }

    onSubmit() {
        if (this.formGroup.valid) {
            this.loading = true;
            const controls = this.formGroup.controls;

            const request: ResetPasswordRequest = {
                email: controls.email.value!,
                password: controls.confirmPassword.value!,
                confirmationCode: controls.verificationCode.value!,
            };
            this.authService.resetPassword(request).subscribe({
                next: () => this.toastService.showSuccess("Password reset", "Your password has been successfully reset!"),
                error: () => {
                    this.loading = false;
                    this.toastService.showDanger("Request failed", "Please try again.");
                },
                complete: () => {
                    this.loading = false;
                    this.router.navigate(['account', 'login']).then();
                },
            });
        }
    }
}
